/**
 * devModeGuardService.js
 * 
 * Servicio modular para la detección en tiempo real de:
 * - Modo Desarrollador (Developer Options)
 * - Ubicaciones Simuladas (Mock Locations)
 * 
 * Funciona únicamente en la APK nativa (Android con Capacitor).
 * En navegadores web / escritorio se deshabilita para permitir el desarrollo.
 */

import { registerPlugin } from '@capacitor/core';
import { isNativePlatform } from '../utils/envUtils';

// Instanciar plugin nativo de forma robusta
let NativePlugin = null;
try {
  NativePlugin = registerPlugin('DeviceStatusPlugin');
} catch (e) {
  // Ignorar si falla el registro inicial
}

/**
 * Consulta el estado actual de modo desarrollador y mock locations en el dispositivo nativo.
 * @returns {Promise<{ isDevMode: boolean, isMockLocation: boolean, isNative: boolean }>}
 */
export async function checkDevModeStatus() {
  try {
    // 1. Si no es plataforma móvil nativa, no bloquear (permitir web/desktop)
    const isNative = isNativePlatform() || 
      (typeof window !== 'undefined' && !!window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform());

    if (!isNative) {
      return { isDevMode: false, isMockLocation: false, isNative: false };
    }

    // 2. Obtener referencia al plugin nativo
    const plugin = 
      NativePlugin || 
      (typeof window !== 'undefined' && window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.DeviceStatusPlugin);

    if (!plugin || typeof plugin.checkStatus !== 'function') {
      console.warn('⚠️ [DevModeGuard] DeviceStatusPlugin no está disponible');
      return { isDevMode: false, isMockLocation: false, isNative: true };
    }

    // 3. Ejecutar comprobación nativa
    const result = await plugin.checkStatus();
    const isDevMode = !!(result && result.isDevMode);
    const isMockLocation = !!(result && result.isMockLocation);

    return {
      isDevMode,
      isMockLocation,
      isNative: true
    };
  } catch (error) {
    console.error('❌ [DevModeGuard] Error al consultar estado del dispositivo:', error);
    return { isDevMode: false, isMockLocation: false, isNative: true };
  }
}

/**
 * Inicia la supervisión continua del estado de Modo Desarrollador.
 * Detecta cambios tanto por intervalo de tiempo como cuando la app vuelve a primer plano
 * (por ejemplo, si el usuario fue a Ajustes de Android, activó modo desarrollador y regresó).
 * 
 * @param {Function} onStatusChange Callback que recibe { isDevMode, isMockLocation, isNative }
 * @param {number} intervalMs Frecuencia de chequeo en milisegundos (default 2000ms)
 * @returns {Function} Función para cancelar la supervisión
 */
export function startDevModeWatcher(onStatusChange, intervalMs = 2000) {
  if (typeof onStatusChange !== 'function') return () => {};

  let isRunning = true;

  const performCheck = async () => {
    if (!isRunning) return;
    const status = await checkDevModeStatus();
    if (!isRunning) return;
    onStatusChange(status);
  };

  // Chequeo inmediato
  performCheck();

  // Chequeo periódico cada X segundos
  const timerId = setInterval(performCheck, intervalMs);

  // Chequeo al volver a la app (cuando el usuario regresa de Ajustes)
  const handleVisibilityChange = () => {
    if (typeof document !== 'undefined' && !document.hidden) {
      performCheck();
    }
  };

  const handleWindowFocus = () => {
    performCheck();
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', handleWindowFocus);
  }

  // Cleanup
  return () => {
    isRunning = false;
    clearInterval(timerId);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', handleWindowFocus);
    }
  };
}
